import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";

import LinkPicker from "../../../../../../components/ui/link-picker/LinkPicker";
import {
  Time,
  Date,
  ButtonGroup,
  FormatPicker,
  TextInput as Text,
  TextAreaInput as TextArea,
  ImagePicker,
} from "@caelor/ui";
import IconPicker from "../../../../../../components/ui/icon-picker/IconPicker";
import GoBack from "../../components/GoBack";
import ColorPicker from "../../../../../../components/ui/color-picker/ColorPicker";

import {
  ANNOUNCEMENT_PROP,
  ANNOUNCEMENTS_CONFIG_PROP,
  CONTENT_BASED_ON_DESIGN,
  DESIGN_STYLES,
} from "./defaults";
import {
  OptionContainer,
  OptionContent,
  OptionLabel,
  SettingsOptionContainer,
  SettingsOptionHeading,
} from "../../styles";
import { BOOLEAN_OPTIONS } from "./options";
import { useAnnouncementsStore } from "../../../../../../stores/use-announcements-store";
import { ImageFocalPoint } from "@lemoncode/react-image-focal-point";
import { useState } from "react";

function AnnouncementOptions() {
  const { t } = useTranslation();
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const {
    data,
    config,
    selectedAnnouncement,
    setSelectedAnnouncement,
    updateAnnouncement,
    updateAnnouncementsIcon,
    updateSingleAnnouncementColor,
    updateConfig,
  } = useAnnouncementsStore((state) => ({
    data: state.data,
    config: state.config,
    selectedAnnouncement: state.selectedAnnouncement,
    setSelectedAnnouncement: state.setSelectedAnnouncement,
    updateAnnouncement: state.updateAnnouncement,
    updateAnnouncementsIcon: state.updateAnnouncementsIcon,
    updateSingleAnnouncementColor: state.updateSingleAnnouncementColor,
    updateConfig: state.updateConfig,
  }));

  const announcement = useMemo(
    () => data.find((item) => item.id === selectedAnnouncement),
    [selectedAnnouncement, data]
  );

  return (
    <>
      <GoBack
        onClick={() => setSelectedAnnouncement(undefined)}
        content={t("selected-card-content")}
      />

      {(config.designStyle === DESIGN_STYLES.BACKGROUND ||
        config.designStyle === DESIGN_STYLES.IMAGE) && (
        <SettingsOptionContainer>
          <SettingsOptionHeading>{t("image")}</SettingsOptionHeading>

          <ImagePicker
            asParent={false}
            image={announcement[ANNOUNCEMENT_PROP.IMAGE]}
            open={imagePickerOpen}
            hideCurrentPage={false}
            setOpen={setImagePickerOpen}
            onChange={(value) =>
              updateAnnouncement({ key: ANNOUNCEMENT_PROP.IMAGE, value })
            }
            imageReposition={true}
            ImageRepositionComponent={ImageFocalPoint}
            tabs={["Unsplash", "From page", "Link", "Upload"]}
            sidebarContainerStyles={{}}
            thumbnailContainerStyles={{}}
            libraryImages={[]}
            iconLibraryImages={[]}
            hideInfo={true}
          />

          {config.designStyle === DESIGN_STYLES.BACKGROUND && (
            <OptionContainer>
              <OptionLabel>{t("overlay")}</OptionLabel>
              <OptionContent>
                <ButtonGroup
                  items={BOOLEAN_OPTIONS}
                  value={announcement[ANNOUNCEMENT_PROP.OVERLAY].isActive}
                  onClick={(isOn) => {
                    const value = {
                      ...announcement[ANNOUNCEMENT_PROP.OVERLAY],
                      isActive: isOn,
                    };

                    updateAnnouncement({
                      key: ANNOUNCEMENT_PROP.OVERLAY,
                      value,
                    });
                  }}
                  i18n={t}
                />
              </OptionContent>
            </OptionContainer>
          )}
        </SettingsOptionContainer>
      )}

      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("link")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel alignToTop>{t("link-to")}</OptionLabel>
          <OptionContent>
            <LinkPicker
              value={announcement[ANNOUNCEMENT_PROP.LINK]}
              onChange={(value) =>
                updateAnnouncement({
                  key: ANNOUNCEMENT_PROP.LINK,
                  value,
                })
              }
            />
          </OptionContent>
        </OptionContainer>
        {announcement[ANNOUNCEMENT_PROP.LINK]?.source !== "email" && (
          <OptionContainer>
            <OptionLabel>{t("new-tab")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                i18n={t}
                items={BOOLEAN_OPTIONS}
                value={announcement[ANNOUNCEMENT_PROP.NEW_TAB] === "true"}
                onClick={(isOn) =>
                  updateAnnouncement({
                    key: ANNOUNCEMENT_PROP.NEW_TAB,
                    value: isOn ? "true" : "false",
                  })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}
      </SettingsOptionContainer>

      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("content")}</SettingsOptionHeading>
        {config.designStyle === DESIGN_STYLES.NOTIFICATION && (
          <OptionContainer>
            <OptionLabel>{t("icon")}</OptionLabel>
            <OptionContent>
              <IconPicker
                defaultTab={t("icons")}
                tabs={[t("icons"), t("emojis"), t("logos")]}
                icon={
                  announcement[ANNOUNCEMENT_PROP.EMOJI_ENABLED]
                    ? announcement[ANNOUNCEMENT_PROP.EMOJI]
                    : announcement[ANNOUNCEMENT_PROP.ICON]
                }
                onChange={({ value, type }) =>
                  updateAnnouncementsIcon({
                    value,
                    type: type.replace("app", "icon"),
                  })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}
        {CONTENT_BASED_ON_DESIGN[config.designStyle]
          ?.filter(
            ({ layout }) =>
              typeof layout === "undefined" ||
              config.layoutStructure.includes(layout)
          )
          ?.map(({ prop, title, type }) => (
            <OptionContainer
              alignItems={
                type === "time" || type === "textarea" ? "flex-start" : "center"
              }
              key={title}
            >
              <OptionLabel
                style={{ lineHeight: type === "time" ? "40px" : "1.1" }}
              >
                {t(title)}
              </OptionLabel>
              <OptionContent>
                {type === "input" && (
                  <Text
                    placeholder={t("add")}
                    value={announcement[prop]}
                    onChange={(value) =>
                      updateAnnouncement({ key: prop, value })
                    }
                  />
                )}
                {type === "textarea" && (
                  <TextArea
                    placeholder={t("add")}
                    value={announcement[prop]}
                    onChange={({ target: { value } }) =>
                      updateAnnouncement({ key: prop, value })
                    }
                    disableNewLine={
                      config[ANNOUNCEMENTS_CONFIG_PROP.DESIGN_STYLE] ===
                      DESIGN_STYLES.BACKGROUND
                    }
                  />
                )}
                {type === "date" && (
                  <Date
                    value={announcement[prop]}
                    onChange={(value) =>
                      updateAnnouncement({ key: prop, value })
                    }
                  />
                )}
                {type === "time" && (
                  <div>
                    <Time
                      value={announcement[prop]}
                      onChange={(value) =>
                        updateAnnouncement({ key: prop, value })
                      }
                      timeFormat={config[ANNOUNCEMENTS_CONFIG_PROP.DATE_FORMAT]}
                    />
                    <FormatPicker
                      value={config[ANNOUNCEMENTS_CONFIG_PROP.DATE_FORMAT]}
                      setValue={(value) =>
                        updateConfig({
                          key: ANNOUNCEMENTS_CONFIG_PROP.DATE_FORMAT,
                          value,
                        })
                      }
                    />
                  </div>
                )}
              </OptionContent>
            </OptionContainer>
          ))}
      </SettingsOptionContainer>

      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("styling-options")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel alignToTop>{t("colors")}</OptionLabel>
          <OptionContent>
            <ColorPicker
              value={{
                accent: announcement[ANNOUNCEMENT_PROP.COLOR] || {},
                text: announcement[ANNOUNCEMENT_PROP.TEXT_COLOR] || {},
              }}
              onChange={(color) => {
                updateSingleAnnouncementColor(color);
              }}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>
    </>
  );
}

export default AnnouncementOptions;
